import React, { useState, useEffect } from "react";
import { Settings, Edit, Plus, Trash2, X } from "lucide-react";

export default function ComplianceSetup() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null);

  const [pfConfig, setPfConfig] = useState({
    enabled: true,
    employeeRate: 12,
    employerRate: 12,
    wageCeiling: 15000,
    cap1800: true,
  });

  const [esiConfig, setEsiConfig] = useState({
    enabled: false,
    employeeRate: 0.75,
    employerRate: 3.25,
    threshold: 21000,
  });

  const [ptConfig, setPtConfig] = useState({
    enabled: true,
    state: "Maharashtra",
    slabs: [
      { upto: 7500, tax: 0 },
      { upto: 10000, tax: 175 },
      { upto: Infinity, tax: 200 },
    ],
  });

  const [tdsConfig, setTdsConfig] = useState({
    regime: "new",
    cessPct: 4,
    slabs: [
      { upto: 300000, rate: 0 },
      { upto: 700000, rate: 5 },
      { upto: 1000000, rate: 10 },
      { upto: 1250000, rate: 15 },
      { upto: 1500000, rate: 20 },
      { upto: Infinity, rate: 30 },
    ],
  });

  function openDrawer(mode) {
    setDrawerMode(mode);
    setDrawerOpen(true);
  }

  return (
    <div className="p-6 md:p-8 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* HEADER */}
        <header>
          <h1 className="text-2xl font-semibold flex items-center gap-3">
            <Settings className="text-teal-600" /> PF / ESI / PT / TDS Setup
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Configure statutory compliance settings for India payroll.
          </p>
        </header>

        {/* CARDS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* PF SETTINGS */}
          <div className="p-6 bg-white rounded-xl shadow">
            <h2 className="font-semibold text-lg mb-4">Provident Fund (PF)</h2>

            <div className="text-sm space-y-2">
              <p>PF Enabled: {pfConfig.enabled ? "Yes" : "No"}</p>
              <p>Employee Rate: {pfConfig.employeeRate}%</p>
              <p>Employer Rate: {pfConfig.employerRate}%</p>
              <p>Wage Ceiling: ₹{pfConfig.wageCeiling.toLocaleString()}</p>
              <p>₹1800 Cap: {pfConfig.cap1800 ? "Enabled" : "Disabled"}</p>
            </div>

            <button
              onClick={() => openDrawer("pf")}
              className="mt-4 flex items-center gap-2 px-3 py-2 border rounded text-sm"
            >
              <Edit size={16} /> Edit PF Settings
            </button>
          </div>

          {/* ESI SETTINGS */}
          <div className="p-6 bg-white rounded-xl shadow">
            <h2 className="font-semibold text-lg mb-4">Employees' State Insurance (ESI)</h2>

            <div className="text-sm space-y-2">
              <p>ESI Enabled: {esiConfig.enabled ? "Yes" : "No"}</p>
              <p>Employee Rate: {esiConfig.employeeRate}%</p>
              <p>Employer Rate: {esiConfig.employerRate}%</p>
              <p>Threshold Limit: ₹{esiConfig.threshold.toLocaleString()}</p>
            </div>

            <button
              onClick={() => openDrawer("esi")}
              className="mt-4 flex items-center gap-2 px-3 py-2 border rounded text-sm"
            >
              <Edit size={16} /> Edit ESI Settings
            </button>
          </div>

          {/* PROFESSIONAL TAX SETTINGS */}
          <div className="p-6 bg-white rounded-xl shadow md:col-span-2">
            <h2 className="font-semibold text-lg mb-4">Professional Tax (PT)</h2>

            <div className="space-y-2 text-sm">
              <p>PT Enabled: {ptConfig.enabled ? "Yes" : "No"}</p>
              <p>State: {ptConfig.state}</p>

              <div className="border rounded p-3 bg-gray-50">
                <h3 className="text-sm font-medium mb-2">Slabs</h3>
                {ptConfig.slabs.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b">
                    <span>
                      Upto {s.upto === Infinity ? "∞" : `₹${s.upto.toLocaleString()}`}
                    </span>
                    <span>Tax: ₹{s.tax}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => openDrawer("pt")}
              className="mt-4 flex items-center gap-2 px-3 py-2 border rounded text-sm"
            >
              <Edit size={16} /> Edit PT Settings
            </button>
          </div>

          {/* TDS SETTINGS */}
          <div className="p-6 bg-white rounded-xl shadow md:col-span-2">
            <h2 className="font-semibold text-lg mb-4">TDS (Income Tax)</h2>

            <div className="text-sm space-y-2">
              <p>Regime: {tdsConfig.regime === "new" ? "New Regime" : "Old Regime"}</p>
              <p>Cess: {tdsConfig.cessPct}%</p>

              <div className="border rounded p-3 bg-gray-50">
                <h3 className="text-sm font-medium mb-2">Tax Slabs</h3>
                {tdsConfig.slabs.map((s, i) => (
                  <div key={i} className="flex justify-between py-1 border-b text-sm">
                    <span>
                      upto {s.upto === Infinity ? "∞" : `₹${s.upto.toLocaleString()}`}
                    </span>
                    <span>{s.rate}%</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => openDrawer("tds")}
              className="mt-4 flex items-center gap-2 px-3 py-2 border rounded text-sm"
            >
              <Edit size={16} /> Edit TDS Settings
            </button>
          </div>

        </div>
      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <Drawer onClose={() => setDrawerOpen(false)}>
          {drawerMode === "pf" && (
            <PfEditor pfConfig={pfConfig} setPfConfig={setPfConfig} onClose={() => setDrawerOpen(false)} />
          )}

          {drawerMode === "esi" && (
            <EsiEditor esiConfig={esiConfig} setEsiConfig={setEsiConfig} onClose={() => setDrawerOpen(false)} />
          )}

          {drawerMode === "pt" && (
            <PtEditor ptConfig={ptConfig} setPtConfig={setPtConfig} onClose={() => setDrawerOpen(false)} />
          )}

          {drawerMode === "tds" && (
            <TdsEditor tdsConfig={tdsConfig} setTdsConfig={setTdsConfig} onClose={() => setDrawerOpen(false)} />
          )}
        </Drawer>
      )}
    </div>
  );
}

/* ---------------------------- DRAWER COMPONENT ---------------------------- */
function Drawer({ children, onClose }) {
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 h-full w-full md:w-2/5 bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Edit Settings</h3>
          <button className="p-2 hover:bg-gray-100 rounded" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </aside>
    </>
  );
}

/* ------------------------- PF EDITOR ------------------------- */
function PfEditor({ pfConfig, setPfConfig, onClose }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">PF Configuration</h2>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={pfConfig.enabled}
          onChange={(e) => setPfConfig((p) => ({ ...p, enabled: e.target.checked }))}
        />
        PF Enabled
      </label>

      <div>
        <label className="text-sm">Employee Contribution (%)</label>
        <input
          type="number"
          value={pfConfig.employeeRate}
          onChange={(e) => setPfConfig((p) => ({ ...p, employeeRate: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <div>
        <label className="text-sm">Employer Contribution (%)</label>
        <input
          type="number"
          value={pfConfig.employerRate}
          onChange={(e) => setPfConfig((p) => ({ ...p, employerRate: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <div>
        <label className="text-sm">Wage Ceiling</label>
        <input
          type="number"
          value={pfConfig.wageCeiling}
          onChange={(e) => setPfConfig((p) => ({ ...p, wageCeiling: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={pfConfig.cap1800}
          onChange={(e) => setPfConfig((p) => ({ ...p, cap1800: e.target.checked }))}
        />
        Apply ₹1800 Maximum Employee PF
      </label>

      <button className="w-full bg-teal-600 text-white py-2 rounded mt-4" onClick={onClose}>
        Save Settings
      </button>
    </div>
  );
}

/* ------------------------- ESI EDITOR ------------------------- */
function EsiEditor({ esiConfig, setEsiConfig, onClose }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">ESI Configuration</h2>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={esiConfig.enabled}
          onChange={(e) => setEsiConfig((p) => ({ ...p, enabled: e.target.checked }))}
        />
        ESI Enabled
      </label>

      <div>
        <label className="text-sm">Employee Rate (%)</label>
        <input
          type="number"
          value={esiConfig.employeeRate}
          onChange={(e) => setEsiConfig((p) => ({ ...p, employeeRate: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <div>
        <label className="text-sm">Employer Rate (%)</label>
        <input
          type="number"
          value={esiConfig.employerRate}
          onChange={(e) => setEsiConfig((p) => ({ ...p, employerRate: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <div>
        <label className="text-sm">Threshold Limit</label>
        <input
          type="number"
          value={esiConfig.threshold}
          onChange={(e) => setEsiConfig((p) => ({ ...p, threshold: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <button className="w-full bg-teal-600 text-white py-2 rounded mt-4" onClick={onClose}>
        Save Settings
      </button>
    </div>
  );
}

/* ------------------------- PT EDITOR ------------------------- */
function PtEditor({ ptConfig, setPtConfig, onClose }) {
  const updateSlab = (i, key, value) => {
    const updated = [...ptConfig.slabs];
    updated[i][key] = value;
    setPtConfig((p) => ({ ...p, slabs: updated }));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Professional Tax (PT)</h2>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={ptConfig.enabled}
          onChange={(e) => setPtConfig((p) => ({ ...p, enabled: e.target.checked }))}
        />
        PT Enabled
      </label>

      <div>
        <label className="text-sm">State</label>
        <select
          value={ptConfig.state}
          onChange={(e) => setPtConfig((p) => ({ ...p, state: e.target.value }))}
          className="w-full mt-1 border p-2 rounded"
        >
          <option>Maharashtra</option>
          <option>Karnataka</option>
          <option>Telangana</option>
          <option>West Bengal</option>
          <option>Tamil Nadu</option>
        </select>
      </div>

      <div className="border p-3 rounded bg-gray-50">
        <h3 className="text-sm font-medium mb-2">Slabs</h3>
        {ptConfig.slabs.map((s, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <input
              type="number"
              value={s.upto === Infinity ? "" : s.upto}
              placeholder="Upto salary"
              onChange={(e) =>
                updateSlab(i, "upto", e.target.value === "" ? Infinity : Number(e.target.value))
              }
              className="border p-2 rounded w-1/2 text-sm"
            />
            <input
              type="number"
              value={s.tax}
              placeholder="Tax amount"
              onChange={(e) => updateSlab(i, "tax", Number(e.target.value))}
              className="border p-2 rounded w-1/2 text-sm"
            />
            <button
              onClick={() => {
                const updated = ptConfig.slabs.filter((_, idx) => idx !== i);
                setPtConfig((p) => ({ ...p, slabs: updated }));
              }}
            >
              <Trash2 size={16} className="text-red-600" />
            </button>
          </div>
        ))}

        <button
          className="flex items-center gap-2 mt-2 px-3 py-1 border rounded text-sm"
          onClick={() =>
            setPtConfig((p) => ({
              ...p,
              slabs: [...p.slabs, { upto: Infinity, tax: 0 }],
            }))
          }
        >
          <Plus size={16} /> Add Slab
        </button>
      </div>

      <button className="w-full bg-teal-600 text-white py-2 rounded mt-4" onClick={onClose}>
        Save Settings
      </button>
    </div>
  );
}

/* ------------------------- TDS EDITOR ------------------------- */
function TdsEditor({ tdsConfig, setTdsConfig, onClose }) {
  const updateSlab = (i, key, value) => {
    const updated = [...tdsConfig.slabs];
    updated[i][key] = value;
    setTdsConfig((p) => ({ ...p, slabs: updated }));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">TDS / Income Tax Configuration</h2>

      <div>
        <label className="text-sm">Active Tax Regime</label>
        <select
          value={tdsConfig.regime}
          onChange={(e) => setTdsConfig((p) => ({ ...p, regime: e.target.value }))}
          className="w-full border mt-1 p-2 rounded"
        >
          <option value="new">New Regime</option>
          <option value="old">Old Regime</option>
        </select>
      </div>

      <div>
        <label className="text-sm">Cess (%)</label>
        <input
          type="number"
          value={tdsConfig.cessPct}
          onChange={(e) => setTdsConfig((p) => ({ ...p, cessPct: Number(e.target.value) }))}
          className="w-full mt-1 border p-2 rounded"
        />
      </div>

      <div className="border p-3 rounded bg-gray-50">
        <h3 className="text-sm font-medium mb-2">Tax Slabs</h3>
        {tdsConfig.slabs.map((s, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <input
              type="number"
              value={s.upto === Infinity ? "" : s.upto}
              onChange={(e) =>
                updateSlab(i, "upto", e.target.value === "" ? Infinity : Number(e.target.value))
              }
              placeholder="Upto income"
              className="border p-2 rounded w-1/2 text-sm"
            />
            <input
              type="number"
              value={s.rate}
              onChange={(e) => updateSlab(i, "rate", Number(e.target.value))}
              placeholder="Rate %"
              className="border p-2 rounded w-1/2 text-sm"
            />
            <button
              onClick={() => {
                const updated = tdsConfig.slabs.filter((_, idx) => idx !== i);
                setTdsConfig((p) => ({ ...p, slabs: updated }));
              }}
            >
              <Trash2 size={16} className="text-red-600" />
            </button>
          </div>
        ))}

        <button
          onClick={() =>
            setTdsConfig((p) => ({
              ...p,
              slabs: [...p.slabs, { upto: Infinity, rate: 30 }],
            }))
          }
          className="flex items-center gap-2 mt-2 px-3 py-1 border rounded text-sm"
        >
          <Plus size={16} /> Add Slab
        </button>
      </div>

      <button className="w-full bg-teal-600 text-white py-2 rounded mt-4" onClick={onClose}>
        Save Settings
      </button>
    </div>
  );
}
